import React, { useState } from 'react';
import {
  Box,
  Button,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  useToast,
  FormLabel,
  FormControl,
  Select,
  SimpleGrid,
  IconButton,
  Divider,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import {
  listInvoiceTemplates,
  saveInvoiceTemplate,
  deleteInvoiceTemplate,
} from '../../../utils/invoiceTemplates';
import { useNavigate } from 'react-router-dom';

const emptyItem = () => ({
  concepto: '',
  unidad: 'un',
  cantidad: 1,
  precio: 0,
  cod_item: '',
  indicador_facturacion: 3,
});

const defaultFormState = () => ({
  moneda: 'UYU',
  forma_pago: 1,
  adendaTexto: '',
  items: [emptyItem()],
});

export default function InvoiceTemplatesPage() {
  const [templates, setTemplates] = useState(() => listInvoiceTemplates());
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [form, setForm] = useState(defaultFormState);
  const toast = useToast();
  const navigate = useNavigate();

  const refresh = () => setTemplates(listInvoiceTemplates());

  const updateItem = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }));
  };

  const removeItem = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== index) : prev.items,
    }));
  };

  const buildPayload = () => ({
    tipo_comprobante: 111,
    moneda: form.moneda,
    forma_pago: Number(form.forma_pago),
    items: form.items
      .filter((item) => item.concepto?.trim())
      .map((item) => ({
        ...item,
        cantidad: Number(item.cantidad) || 1,
        precio: Number(item.precio) || 0,
      })),
    adenda: { texto: form.adendaTexto },
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast({ title: 'Nombre requerido', status: 'warning', duration: 2500 });
      return;
    }
    const payload = buildPayload();
    if (payload.items.length === 0) {
      toast({
        title: 'Agregá al menos un ítem con descripción',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    saveInvoiceTemplate({ name: name.trim(), description, payload });
    refresh();
    setName('');
    setDescription('');
    setForm(defaultFormState());
    toast({ title: 'Plantilla guardada', status: 'success', duration: 2500 });
  };

  const handleEdit = (tpl) => {
    setName(tpl.name);
    setDescription(tpl.description || '');
    const p = tpl.payload || {};
    setForm({
      moneda: p.moneda || 'UYU',
      forma_pago: p.forma_pago || 1,
      adendaTexto: p.adenda?.texto || '',
      items:
        p.items?.length > 0
          ? p.items.map((item) => ({
              concepto: item.concepto || '',
              unidad: item.unidad || 'un',
              cantidad: item.cantidad ?? 1,
              precio: item.precio ?? 0,
              cod_item: item.cod_item || '',
              indicador_facturacion: item.indicador_facturacion ?? 3,
            }))
          : [emptyItem()],
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUse = (tpl) => {
    sessionStorage.setItem('invoice_template_draft', JSON.stringify(tpl.payload));
    navigate('/admin/create-invoices');
    toast({ title: 'Plantilla cargada en emitir factura', status: 'info', duration: 2500 });
  };

  return (
    <AdminPageShell
      title="Plantillas de factura"
      actions={
        <Button size="sm" variant="outline" onClick={() => navigate('/admin/create-invoices')}>
          Ir a emitir factura
        </Button>
      }
    >
      <VStack align="stretch" spacing={5} maxW="800px">
        <Text fontSize="sm" color="gray.600">
          Armá plantillas con los datos que usás seguido (servicios, repuestos, textos de adenda).
          Al emitir una factura podés cargar una plantilla y completar solo el cliente.
        </Text>

        <Box p={4} borderWidth="1px" borderRadius="md" bg="white">
          <Text fontWeight="semibold" fontSize="sm" mb={3}>
            Nueva plantilla
          </Text>
          <VStack align="stretch" spacing={3}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Nombre</FormLabel>
              <Input
                size="sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Servicio taller estándar"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Descripción (opcional)</FormLabel>
              <Input
                size="sm"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Para recordar cuándo usarla"
              />
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl>
                <FormLabel fontSize="sm">Moneda</FormLabel>
                <Select
                  size="sm"
                  value={form.moneda}
                  onChange={(e) => setForm((p) => ({ ...p, moneda: e.target.value }))}
                >
                  <option value="UYU">Pesos (UYU)</option>
                  <option value="USD">Dólares (USD)</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Forma de pago</FormLabel>
                <Select
                  size="sm"
                  value={form.forma_pago}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, forma_pago: Number(e.target.value) }))
                  }
                >
                  <option value={1}>Contado</option>
                  <option value={2}>Crédito</option>
                </Select>
              </FormControl>
            </SimpleGrid>

            <Divider />

            <HStack justify="space-between">
              <Text fontSize="sm" fontWeight="semibold">
                Ítems de la factura
              </Text>
              <Button size="xs" leftIcon={<AddIcon />} onClick={addItem}>
                Agregar ítem
              </Button>
            </HStack>

            {form.items.map((item, index) => (
              <Box key={index} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                    Ítem {index + 1}
                  </Text>
                  {form.items.length > 1 && (
                    <IconButton
                      size="xs"
                      aria-label="Eliminar ítem"
                      icon={<DeleteIcon />}
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeItem(index)}
                    />
                  )}
                </HStack>
                <VStack align="stretch" spacing={2}>
                  <FormControl isRequired>
                    <FormLabel fontSize="xs">Descripción / concepto</FormLabel>
                    <Input
                      size="sm"
                      value={item.concepto}
                      onChange={(e) => updateItem(index, 'concepto', e.target.value)}
                      placeholder="Ej: Mano de obra service"
                    />
                  </FormControl>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
                    <FormControl>
                      <FormLabel fontSize="xs">Cantidad</FormLabel>
                      <NumberInput
                        size="sm"
                        min={0}
                        value={item.cantidad}
                        onChange={(_, v) => updateItem(index, 'cantidad', v)}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Precio unitario</FormLabel>
                      <NumberInput
                        size="sm"
                        min={0}
                        value={item.precio}
                        onChange={(_, v) => updateItem(index, 'precio', v)}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Unidad</FormLabel>
                      <Select
                        size="sm"
                        value={item.unidad}
                        onChange={(e) => updateItem(index, 'unidad', e.target.value)}
                      >
                        <option value="un">Unidad</option>
                        <option value="h">Hora</option>
                        <option value="kg">Kilo</option>
                        <option value="lt">Litro</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Cód. producto</FormLabel>
                      <Input
                        size="sm"
                        value={item.cod_item}
                        onChange={(e) => updateItem(index, 'cod_item', e.target.value)}
                        placeholder="Opcional"
                      />
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              </Box>
            ))}

            <FormControl>
              <FormLabel fontSize="sm">Texto de adenda (opcional)</FormLabel>
              <Textarea
                size="sm"
                rows={3}
                value={form.adendaTexto}
                onChange={(e) => setForm((p) => ({ ...p, adendaTexto: e.target.value }))}
                placeholder="Observaciones que aparecen al pie de la factura"
              />
            </FormControl>

            <Button size="sm" variant="adminPrimary" alignSelf="flex-start" onClick={handleSave}>
              Guardar plantilla
            </Button>
          </VStack>
        </Box>

        <Divider />

        <Text fontWeight="semibold" fontSize="sm">
          Plantillas guardadas
        </Text>
        {templates.length === 0 ? (
          <Text fontSize="sm" color="gray.500">
            Sin plantillas guardadas.
          </Text>
        ) : (
          templates.map((tpl) => (
            <Box key={tpl.id} p={3} borderWidth="1px" borderRadius="md">
              <Text fontWeight="semibold" fontSize="sm">
                {tpl.name}
              </Text>
              {tpl.description && (
                <Text fontSize="xs" color="gray.500">
                  {tpl.description}
                </Text>
              )}
              <Text fontSize="xs" color="gray.500" mt={1}>
                {tpl.payload?.items?.length || 0} ítem(s) · {tpl.payload?.moneda || 'UYU'} ·{' '}
                {tpl.payload?.forma_pago === 2 ? 'Crédito' : 'Contado'}
              </Text>
              <HStack mt={2} spacing={2}>
                <Button size="xs" variant="adminPrimary" onClick={() => handleUse(tpl)}>
                  Usar
                </Button>
                <Button size="xs" variant="outline" onClick={() => handleEdit(tpl)}>
                  Editar
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => {
                    deleteInvoiceTemplate(tpl.id);
                    refresh();
                  }}
                >
                  Eliminar
                </Button>
              </HStack>
            </Box>
          ))
        )}
      </VStack>
    </AdminPageShell>
  );
}
